import React, { useContext, useEffect, useState } from 'react'

import { SelectInputV3, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import { CTAContainer } from 'components/CTAContainer'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { ActionType, EmploymentInput, Occupation, PageName, User } from 'generated/types'
import { useCardStatusRedirect } from 'pages/CardOrderReview/useCardStatusPolling'
import { TestIDs } from 'test/utils/testIds'
import { storeEmploymentStatus } from 'utils/employmentStatus'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'

import { useOccupationTracking } from './events'
import OccupationsQuery from './gql/occupations.gql'
import UpdateOccupationMutation from './gql/update-occupation.gql'
import { Content, Dropdowns, HeroHeader, HeroText } from './styled-components'
import { filterNFormatOccupationList, formatEmploymentStatusList } from './utils'

type Props = WithFlowRouterProps & HeroTextProps

type OccupationsQueryData = {
  occupations: Occupation[]
  user: {
    occupation: string
  }
  bankingEmploymentStatuses: string[]
}

enum EmploymentStatus {
  Employed = 'Employed',
  SelfEmployed = 'Self Employed',
  Student = 'Student',
  Retired = 'Retired',
  Unemployed = 'Unemployed',
}

const occupationsToFilterOut = ['Agriculture', 'Architect or Engineer', 'Management', 'Other']

const NextButton = withClickTrack<any, any>('')(Button)

export const enhance = compose<Props, {}>(withFlowRouter, withHeroText)

export const SelectOccupation: React.FC<Props> = ({ route }) => {
  const { getRemoteString } = useRemoteStrings()
  const [employmentStatus, setEmploymentStatus] = useState('')
  const [occupation, setOccupation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const track = useOccupationTracking()
  const currentlyEmployed =
    employmentStatus === EmploymentStatus.Employed ||
    employmentStatus === EmploymentStatus.SelfEmployed
  const previouslyEmployed =
    employmentStatus === EmploymentStatus.Retired ||
    employmentStatus === EmploymentStatus.Unemployed
  const displayOccupationInput = currentlyEmployed || previouslyEmployed
  const occupationLabel = currentlyEmployed ? 'Occupation' : 'Previous occupation'
  const disableButtonRules =
    !employmentStatus || ((currentlyEmployed || previouslyEmployed) && !occupation) || submitting
  const { data, loading } = useQuery<OccupationsQueryData>(OccupationsQuery)
  const [updateOccupation] = useMutation<Partial<User>, Pick<EmploymentInput, 'occupation'>>(
    UpdateOccupationMutation,
    {
      onCompleted: () => {
        isDynamicFunnelEnabled
          ? routeToPage(PageName.Occupation, ActionType.Continue)
          : route({ from: FlowRoute.Occupation, action: 'continue' })
      },
    },
  )

  const loadingCardStatus = useCardStatusRedirect(() => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.Occupation, ActionType.Skip)
      : route({ from: FlowRoute.Occupation, action: 'skip' })
  })

  const occupations = data?.occupations ?? []
  const employmentStatuses = data?.bankingEmploymentStatuses ?? []

  useEffect(() => {
    if (data?.user?.occupation) {
      setOccupation(data?.user?.occupation)
    }
  }, [data])

  const onEmploymentStatusChange = (name, value: string) => {
    setEmploymentStatus(value)
    if (value === EmploymentStatus.Student) {
      setOccupation(EmploymentStatus.Student)
    } else {
      setOccupation('')
    }
  }

  const onOccupationChange = (name, value: string) => {
    setOccupation(value)
  }

  const handleNextClick = () => {
    track.nextTapped()
    setSubmitting(true)
    storeEmploymentStatus(employmentStatus)
    updateOccupation({
      variables: {
        occupation: occupation,
      },
    })
  }

  if (loading || loadingCardStatus) {
    return <CenteredSpinner />
  }

  return (
    <>
      <Nav back />
      <Layout>
        <Content>
          <HeroHeader data-testid={TestIDs.OCCUPATION.TITLE}>
            {getRemoteString({ key: 'registration.occupation.header' })}
          </HeroHeader>
          <HeroText>{getRemoteString({ key: 'registration.occupation.body' })}</HeroText>
          <Dropdowns>
            <SelectInputV3
              id="employmentStatus"
              label={getRemoteString({
                key: 'registration.occupation.employment_status.picker.label',
              })}
              name="employmentStatus"
              onChange={onEmploymentStatusChange}
              options={formatEmploymentStatusList(employmentStatuses)}
              value={employmentStatus}
              placeholder="Select one"
              disabled={submitting}
            />
            {displayOccupationInput && (
              <SelectInputV3
                id="occupation"
                label={occupationLabel}
                name="occupation"
                onChange={onOccupationChange}
                options={filterNFormatOccupationList(occupations, occupationsToFilterOut)}
                value={occupation}
                placeholder="Select occupation"
                disabled={submitting}
              />
            )}
          </Dropdowns>
        </Content>
        <Separator />
        <CTAContainer>
          <NextButton
            data-testid={TestIDs.OCCUPATION.NEXT_BUTTON}
            disabled={disableButtonRules}
            onPress={handleNextClick}
          >
            {submitting ? (
              <Spinner height={20} width={20} white />
            ) : (
              getRemoteString({ key: 'registration.occupation.cta' })
            )}
          </NextButton>
        </CTAContainer>
      </Layout>
    </>
  )
}

export default enhance(SelectOccupation)
